import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  X as CloseIcon,
  Heart as HeartIcon,
  CalendarBlank as CalendarIcon,
  Carrot as CarrotIcon,
  Palette as PaletteIcon
} from '@phosphor-icons/react';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import SupplierList from '../Chat/SupplierList';

const CoupleInformationModal = ({ open, onClose, chatId }) => {
  const styles = useThemeStyles();
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness || { selectedBusiness: null };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [isRedacted, setIsRedacted] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCoupleInformation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open]);

  const fetchCoupleInformation = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (user?.role === 'supplier' && chatId) {
        // For suppliers, always use the chat-specific endpoint when we have a chatId
        // This ensures proper access control through chat relationships
        const response = await api.get(`/api/chat/conversations/${chatId}/couple-info`);
        setWedding(response.data.wedding);
        setPartnerDetails(response.data.partners);
        setIsRedacted(response.data.isRedacted || false);
      } else {
        // For couples, use the standard endpoints
        const [weddingRes, detailsRes] = await Promise.all([
          api.get('/api/wedding'),
          api.get('/api/wedding-details/partners')
        ]);
        
        setWedding(weddingRes.data);
        setPartnerDetails(detailsRes.data);
        setIsRedacted(false);
      }
    } catch (error) {
      setError('Failed to load couple information');
    } finally {
      setLoading(false);
    }
  };

  const getPartnerInitials = (partner) => {
    if (!partner?.fullName) return '?';
    const names = partner.fullName.split(' ');
    return names.map(name => name[0]).join('').toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const renderPartnerCard = (partner, _title) => {
    if (!partner || (!partner.fullName && !partner.email && !partner.phone)) return null;

    // If redacted, only show basic contact info
    const showRedacted = isRedacted && user?.role === 'supplier';
    
    return (
      <Card sx={{ height: '100%', ...styles.card }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                mr: 2,
                bgcolor: 'primary.main',
                ...styles.avatar
              }}
              src={showRedacted ? null : partner.profilePicture}
            >
              {getPartnerInitials(partner)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                {partner.fullName || 'Name not provided'}
              </Typography>
              {!showRedacted && partner.preferredName && partner.preferredName !== partner.fullName && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Preferred: {partner.preferredName}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          {/* Contact Information - Always shown */}
          {(partner.email || partner.phone) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Contact Information
              </Typography>
              {partner.email && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Email:</strong> {partner.email}
                </Typography>
              )}
              {partner.phone && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Phone:</strong> {partner.phone}
                </Typography>
              )}
            </Box>
          )}
          
          {showRedacted && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ ...styles.text.secondary, fontStyle: 'italic' }}>
                Additional details are restricted until the couple adds you as a supplier.
              </Typography>
            </>
          )}
          
          {!showRedacted && (
            <>
          <Divider sx={{ my: 2 }} />

          {/* Pronouns */}
          {partner.pronouns && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Pronouns
              </Typography>
              <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                {partner.pronouns}
              </Typography>
            </Box>
          )}

          {/* Wedding Role - Only show if there's a role set */}
          {(partner.planningRole || partner.mainWeddingDayContact) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Wedding Role
              </Typography>
              {partner.planningRole && (
                <Chip
                  label={partner.planningRole}
                  size="small"
                  sx={{ mb: 1, ...styles.chip }}
                />
              )}
              {partner.mainWeddingDayContact && (
                <Chip
                  label="Main Wedding Day Contact"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, ...styles.chip }}
                />
              )}
            </Box>
          )}


          {/* Dietary & Accessibility */}
          {(partner.dietaryPreferences || partner.allergies || partner.accessibilityNeeds) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Dietary & Accessibility
              </Typography>
              {partner.dietaryPreferences && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CarrotIcon size={16} style={{ marginRight: 8, color: '#64748b' }} />
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    {partner.dietaryPreferences}
                  </Typography>
                </Box>
              )}
              {partner.allergies && (
                <Typography variant="body2" sx={{ ...styles.text.secondary, color: 'warning.main' }}>
                  Allergies: {partner.allergies}
                </Typography>
              )}
              {partner.accessibilityNeeds && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  Accessibility: {partner.accessibilityNeeds}
                </Typography>
              )}
            </Box>
          )}

          {/* Cultural & Religious Considerations */}
          {(partner.ceremonyType || partner.culturalNotes) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Cultural & Religious Considerations
              </Typography>
              {partner.ceremonyType && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  Ceremony Type: {partner.ceremonyType}
                </Typography>
              )}
              {partner.culturalNotes && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  Cultural Notes: {partner.culturalNotes}
                </Typography>
              )}
            </Box>
          )}

          {/* Personal Touches */}
          {(partner.styleNotes || partner.hobbies || partner.supplierNotes) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Personal Touches
              </Typography>
              {partner.styleNotes && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PaletteIcon size={16} style={{ marginRight: 8, color: '#64748b' }} />
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    Style: {partner.styleNotes}
                  </Typography>
                </Box>
              )}
              {partner.hobbies && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  Hobbies: {partner.hobbies}
                </Typography>
              )}
              {partner.supplierNotes && (
                <Typography variant="body2" sx={{ ...styles.text.secondary, fontStyle: 'italic' }}>
                  Note for suppliers: {partner.supplierNotes}
                </Typography>
              )}
            </Box>
          )}

          {/* Optional Extras */}
          {(partner.proposalDate || partner.plannedWeddingDate || partner.newSurname) && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Optional Extras
              </Typography>
              {partner.proposalDate && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  Proposal Date: {formatDate(partner.proposalDate)}
                </Typography>
              )}
              {partner.plannedWeddingDate && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  Planned Wedding Date: {formatDate(partner.plannedWeddingDate)}
                </Typography>
              )}
              {partner.newSurname && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  New Surname: {partner.newSurname}
                </Typography>
              )}
            </Box>
          )}
          </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          ...styles.dialog
        }
      }}
    >
      <DialogTitle sx={{ ...styles.dialogTitle }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HeartIcon size={24} style={{ marginRight: 12, color: '#e91e63' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
              Couple Information
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ ...styles.dialogContent }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Redaction Notice */}
            {isRedacted && user?.role === 'supplier' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Information Restricted
                </Typography>
                <Typography variant="body2">
                  Wedding information is currently restricted. Once the couple adds you as a supplier in their dashboard, full details will be available.
                </Typography>
              </Alert>
            )}
            {/* Wedding Overview */}
            {wedding && (
              <Card sx={{ mb: 3, ...styles.card }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarIcon size={24} style={{ marginRight: 12, color: '#64748b' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                      Wedding Details
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                        <strong>Wedding Date:</strong> {formatDate(wedding.venues.ceremony.date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                        <strong>Guest Count:</strong> {wedding.guestCount || 'Not specified'}
                      </Typography>
                    </Grid>
                    {wedding.venue && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                          <strong>Venue:</strong> {wedding.venue}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Partner Information */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {renderPartnerCard(partnerDetails?.partnerOne, 'Partner One')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderPartnerCard(partnerDetails?.partnerTwo, 'Partner Two')}
              </Grid>
            </Grid>

            {/* Supplier List - Only show for suppliers */}
            {user?.role === 'supplier' && wedding?._id && (
              <Card sx={{ mt: 3, ...styles.card }}>
                <CardContent>
                  <SupplierList
                    weddingId={wedding._id}
                    currentBusinessId={selectedBusiness?.business?._id}
                    onChatCreated={(chat) => {
                      // Close modal and navigate to chat
                      onClose();
                      window.location.href = `/supplier/messages/${chat._id}`;
                    }}
                  />
                </CardContent>
              </Card>
            )}

          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ ...styles.dialogActions }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoupleInformationModal;
